/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\EslUserGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the ESL Users module.
----------------------------------------------------------------*/


import { Users } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import { SecretInput } from '../../../components/ui/SecretField';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { EslUserFormState } from '../types';

type AccessMode = 'inbound' | 'outbound' | 'embedded';
const ACCESS_MODE_OPTIONS: AccessMode[] = ['inbound', 'outbound', 'embedded'];

interface EslUserGeneralSectionProps {
    formData: EslUserFormState;
    isEditMode: boolean;
    onFieldChange: (field: keyof EslUserFormState, value: EslUserFormState[keyof EslUserFormState]) => void;
    errors?: Record<string, string>;
    t: (key: string) => string;
}

export function EslUserGeneralSection({ formData, isEditMode, onFieldChange, errors = {}, t }: EslUserGeneralSectionProps) {
    return (
        <FormBox title={t('eslUsers.form.sections.general')} icon={<Users className="h-5 w-5" />}>
            <div className="grid grid-cols-8 items-center gap-x-6 gap-y-4">
                {/* Username */}
                <SimpleTooltip content={t('eslUsers.form.fields.username.tooltip')} className="col-span-2">
                    <Label htmlFor="username" className="text-left">{t('eslUsers.form.fields.username.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="username"
                        value={formData.username}
                        onChange={(e) => onFieldChange('username', e.target.value)}
                        placeholder={t('eslUsers.form.fields.username.placeholder')}
                        error={errors.username}
                        required
                    />
                </div>

                {/* Password */}
                <SimpleTooltip content={t('eslUsers.form.fields.password.tooltip')} className="col-span-2">
                    <Label htmlFor="password" className="text-left">{t('eslUsers.form.fields.password.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <SecretInput
                        id="password"
                        value={formData.password}
                        onChange={(value) => onFieldChange('password', value)}
                        placeholder={t('eslUsers.form.fields.password.placeholder')}
                        error={errors.password}
                    />
                    {isEditMode && (
                        <p className="text-xs text-muted-foreground mt-1">{t('eslUsers.form.helpers.passwordOptional')}</p>
                    )}
                </div>

                {/* Access Mode */}
                <SimpleTooltip content={t('eslUsers.form.fields.accessMode.tooltip')} className="col-span-2">
                    <Label htmlFor="accessMode" className="text-left">{t('eslUsers.form.fields.accessMode.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <select
                        id="accessMode"
                        value={formData.accessMode}
                        onChange={(e) => onFieldChange('accessMode', e.target.value as AccessMode)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {ACCESS_MODE_OPTIONS.map((mode) => (
                            <option key={mode} value={mode}>{t(`eslUsers.accessModes.${mode}`)}</option>
                        ))}
                    </select>
                </div>

                {/* Allowed IP */}
                <SimpleTooltip content={t('eslUsers.form.fields.allowedIp.tooltip')} className="col-span-2">
                    <Label htmlFor="allowedIp" className="text-left">{t('eslUsers.form.fields.allowedIp.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="allowedIp"
                        value={formData.allowedIp}
                        onChange={(e) => onFieldChange('allowedIp', e.target.value)}
                        placeholder={t('eslUsers.form.fields.allowedIp.placeholder')}
                    />
                </div>

                {/* Description */}
                <SimpleTooltip content={t('eslUsers.form.fields.description.tooltip')} className="col-span-2">
                    <Label htmlFor="description" className="text-left">{t('eslUsers.form.fields.description.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="description"
                        value={formData.description}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        placeholder={t('eslUsers.form.fields.description.placeholder')}
                    />
                </div>

                {/* Listen IP */}
                <SimpleTooltip content={t('eslUsers.form.fields.listenIp.tooltip')} className="col-span-2">
                    <Label htmlFor="listenIp" className="text-left">{t('eslUsers.form.fields.listenIp.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="listenIp"
                        value={formData.listenIp}
                        onChange={(e) => onFieldChange('listenIp', e.target.value)}
                        placeholder={t('eslUsers.form.fields.listenIp.placeholder')}
                    />
                </div>

                {/* Listen Port */}
                <SimpleTooltip content={t('eslUsers.form.fields.listenPort.tooltip')} className="col-span-2">
                    <Label htmlFor="listenPort" className="text-left">{t('eslUsers.form.fields.listenPort.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="listenPort"
                        type="number"
                        min={1}
                        max={65535}
                        value={formData.listenPort}
                        onChange={(e) => onFieldChange('listenPort', e.target.value)}
                        placeholder={t('eslUsers.form.fields.listenPort.placeholder')}
                    />
                </div>

                {/* Apply Inbound ACL */}
                <SimpleTooltip content={t('eslUsers.form.fields.applyInboundAcl.tooltip')} className="col-span-2">
                    <Label htmlFor="applyInboundAcl" className="text-left">{t('eslUsers.form.fields.applyInboundAcl.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="applyInboundAcl"
                        value={formData.applyInboundAcl}
                        onChange={(e) => onFieldChange('applyInboundAcl', e.target.value)}
                        placeholder={t('eslUsers.form.fields.applyInboundAcl.placeholder')}
                    />
                </div>

                {/* NAT Map */}
                <SimpleTooltip content={t('eslUsers.form.fields.natMap.tooltip')} className="col-span-2">
                    <Label htmlFor="natMap" className="text-left">{t('eslUsers.form.fields.natMap.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2 flex items-center gap-3">
                    <Toggle id="natMap" value={formData.natMap} onChange={(value) => onFieldChange('natMap', value)} />
                    <span className="text-sm text-muted-foreground">
                        {formData.natMap ? t('common.enabled') : t('common.disabled')}
                    </span>
                </div>

                {/* Stop on Bind Error */}
                <SimpleTooltip content={t('eslUsers.form.fields.stopOnBindError.tooltip')} className="col-span-2">
                    <Label htmlFor="stopOnBindError" className="text-left">{t('eslUsers.form.fields.stopOnBindError.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2 flex items-center gap-3">
                    <Toggle id="stopOnBindError" value={formData.stopOnBindError} onChange={(value) => onFieldChange('stopOnBindError', value)} />
                    <span className="text-sm text-muted-foreground">
                        {formData.stopOnBindError ? t('common.enabled') : t('common.disabled')}
                    </span>
                </div>

                {/* Enabled */}
                <SimpleTooltip content={t('eslUsers.form.fields.enabled.tooltip')} className="col-span-2">
                    <Label htmlFor="enabled" className="text-left">{t('eslUsers.form.fields.enabled.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2 flex items-center gap-3">
                    <Toggle id="enabled" value={formData.enabled} onChange={(value) => onFieldChange('enabled', value)} />
                    <span className="text-sm text-muted-foreground">
                        {formData.enabled ? t('common.enabled') : t('common.disabled')}
                    </span>
                </div>
            </div>
        </FormBox>
    );
}
